import SkeletonBone from './SkeletonBone';

const CHART_BARS = ['h-16', 'h-24', 'h-20', 'h-32', 'h-28', 'h-40', 'h-24', 'h-36', 'h-20', 'h-44', 'h-28', 'h-16'];

function ChainChipBone({ captionWidth, labelWidth }) {
  return (
    <div className="inline-flex max-w-[14rem] flex-col rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-sm dark:border-slate-700 dark:bg-slate-950">
      <SkeletonBone className={`h-2 ${captionWidth}`} />
      <SkeletonBone className={`mt-1 h-3 ${labelWidth}`} />
    </div>
  );
}

function ChainLink() {
  return <span className="mx-0.5 hidden h-px w-3 shrink-0 bg-slate-300 sm:inline-block dark:bg-slate-600" />;
}

function StatCardBone() {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
      <SkeletonBone className="h-3 w-24" />
      <SkeletonBone className="mt-2 h-6 w-16" />
      <SkeletonBone className="mt-1.5 h-3 w-32" />
    </div>
  );
}

function ChartSectionSkeleton({ eyebrow, title, subtitle, download = false }) {
  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <SkeletonBone className={`h-3 ${eyebrow}`} />
          <SkeletonBone className={`mt-1.5 h-4 ${title}`} />
          <SkeletonBone className={`mt-1.5 h-3 ${subtitle}`} />
          {download ? <SkeletonBone className="mt-2 h-7 w-40" rounded="rounded-lg" /> : null}
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800/80">
          <SkeletonBone className="h-6 w-16" />
          <SkeletonBone className="h-6 w-10" />
          <SkeletonBone className="h-6 w-14" />
          <SkeletonBone className="h-6 w-14" />
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <StatCardBone />
        <StatCardBone />
        <StatCardBone />
      </div>
      <div className="mt-3 flex h-72 items-stretch gap-2">
        <div className="flex w-8 flex-col justify-between py-3">
          {Array.from({ length: 5 }, (_, index) => (
            <SkeletonBone key={index} className="h-2 w-full" />
          ))}
        </div>
        <div className="flex min-w-0 flex-1 items-end gap-1 border-b border-l border-slate-200 px-1 pb-6 dark:border-slate-700">
          {CHART_BARS.map((height, index) => (
            <SkeletonBone key={index} className={`min-w-0 flex-1 ${height}`} rounded="rounded-sm" />
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineRowBone({ title, subtitle }) {
  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      <span className="relative z-[1] mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-slate-200 ring-2 ring-white dark:bg-slate-700 dark:ring-slate-950" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <SkeletonBone className={`h-4 ${title}`} />
          <SkeletonBone className="h-3 w-28" />
        </div>
        <SkeletonBone className={`mt-1 h-3 ${subtitle}`} />
      </div>
    </li>
  );
}

/** Stand-in for the device history card while a new serial is loading. */
export default function DeviceHistorySkeleton() {
  return (
    <div role="status" aria-live="polite" aria-label="Загрузка истории устройства">
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
        <SkeletonBone className="mb-2 h-3 w-24" />
        <div className="flex flex-wrap items-center gap-y-2">
          <ChainChipBone captionWidth="w-6" labelWidth="w-20" />
          <ChainLink />
          <ChainChipBone captionWidth="w-10" labelWidth="w-28" />
          <ChainLink />
          <ChainChipBone captionWidth="w-10" labelWidth="w-24" />
          <ChainLink />
          <ChainChipBone captionWidth="w-16" labelWidth="w-24" />
          <span className="mx-0.5 h-px w-3 shrink-0 bg-slate-300 dark:bg-slate-600" />
          <div className="flex flex-col gap-1">
            <ChainChipBone captionWidth="w-12" labelWidth="w-24" />
            <ChainChipBone captionWidth="w-12" labelWidth="w-20" />
          </div>
        </div>
      </div>

      <ChartSectionSkeleton eyebrow="w-12" title="w-64" subtitle="w-80" />
      <ChartSectionSkeleton eyebrow="w-16" title="w-72" subtitle="w-80" download />
      <ChartSectionSkeleton eyebrow="w-10" title="w-72" subtitle="w-80" />

      <div className="mt-4 space-y-3">
        <div className="rounded-xl border border-violet-300/40 bg-violet-50/70 px-3 py-2.5 dark:border-violet-500/30 dark:bg-violet-500/10">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <SkeletonBone className="h-3 w-24" />
              <SkeletonBone className="mt-1.5 h-4 w-40" />
              <SkeletonBone className="mt-1 h-3 w-32" />
            </div>
            <SkeletonBone className="h-7 w-40" rounded="rounded-lg" />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/30">
          <SkeletonBone className="mb-2 h-3 w-16" />
          <ul>
            <TimelineRowBone title="w-36" subtitle="w-48" />
            <TimelineRowBone title="w-44" subtitle="w-40" />
            <TimelineRowBone title="w-32" subtitle="w-52" />
            <TimelineRowBone title="w-40" subtitle="w-36" />
            <TimelineRowBone title="w-28" subtitle="w-44" />
          </ul>
        </div>
      </div>
    </div>
  );
}
